import { useEffect, useState } from "react";
import type { RecordingStatus } from "@cuekards/core/recording";
import { IDLE_STATUS } from "@cuekards/core/recording";

/**
 * The 3-2-1 countdown (ticket 13, FR-44), sized against the whole surface it is
 * given — a full-screen window on the desktop.
 *
 * It is not part of the overlay because the overlay is whatever size the user
 * dragged it to, and the countdown has to be read from wherever they are
 * standing when they film — a strip of a prompter would give a strip of a digit.
 *
 * The host decides when this is on screen; it only ever draws whichever number
 * the one recording state says is current, so the digit here and the transport's
 * readout can never disagree.
 */
export function CountdownApp(): JSX.Element | null {
  const [status, setStatus] = useState<RecordingStatus>(IDLE_STATUS);

  useEffect(() => {
    let cancelled = false;
    // The window is shown as `counting` begins, so the first status may already
    // have been broadcast before this page is listening. Pull, then follow.
    void window.cuekards.getRecordingStatus().then((s) => {
      if (!cancelled) setStatus(s);
    });
    const off = window.cuekards.onRecordingStatusChanged(setStatus);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  // The window is hidden outside the countdown, so this is belt-and-braces
  // against a stale digit being on screen for the frame before it goes.
  if (status.phase !== "counting" || status.countdown === undefined) return null;

  return (
    <div className="countdown">
      {/* `key` is the number, so React replaces the element on each tick and the
        * pop animation restarts — a `2` that appeared the way the `3` did is how
        * the user knows the countdown is still running rather than stuck. */}
      <span key={status.countdown} className="countdown-number">
        {status.countdown}
      </span>
    </div>
  );
}
